import { motion, useReducedMotion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import type { RefObject } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { ShortlistCandidateCard } from '@/modules/public-shortlist/components/ShortlistCandidateCard';
import { FEATURED_CANDIDATE_COUNT } from '@/modules/public-shortlist/constants';
import type { ShortlistCandidate } from '@/modules/public-shortlist/mapPublicCandidate';
import {
  getShortlistRevealInitial,
  getShortlistRevealTransition,
  SHORTLIST_LOAD_DELAYS,
  shortlistRevealVisible,
} from '@/modules/public-shortlist/shortlistMotion';
import {
  useShortlistActions,
  useShortlistShowAll,
} from '@/modules/public-shortlist/store';
import { Button } from '@/modules/ui/components/button/Button';

interface ShortlistCandidateListProps {
  candidates: ShortlistCandidate[];
  shortlistKey: string;
  firstCardRef?: RefObject<HTMLDivElement | null>;
}

export function ShortlistCandidateList({
  candidates,
  shortlistKey,
  firstCardRef,
}: ShortlistCandidateListProps) {
  const prefersReducedMotion = useReducedMotion();
  const showAll = useShortlistShowAll(shortlistKey);
  const { setShowAll, saveScrollPosition } = useShortlistActions();
  const { company, roleName } = useParams<{
    company: string;
    roleName: string;
  }>();
  const [searchParams] = useSearchParams();
  const search = searchParams.toString();

  const featured = candidates.slice(0, FEATURED_CANDIDATE_COUNT);
  const remaining = candidates.slice(FEATURED_CANDIDATE_COUNT);
  const nextCandidate = remaining[0];

  const getDetailHref = (candidateJobId: string) => ({
    pathname: `/${company}/shortlist/${roleName}/${candidateJobId}`,
    search: search ? `?${search}` : undefined,
  });

  return (
    <motion.section
      aria-labelledby="shortlist-candidates-heading"
      initial={getShortlistRevealInitial(prefersReducedMotion)}
      animate={shortlistRevealVisible}
      transition={getShortlistRevealTransition(
        prefersReducedMotion,
        SHORTLIST_LOAD_DELAYS.list,
      )}
      className="mt-8"
    >
      <div className="space-y-5 md:space-y-6">
        {(showAll ? candidates : featured).map((candidate, index) => {
          const card = (
            <ShortlistCandidateCard
              candidate={candidate}
              detailHref={getDetailHref(candidate.id)}
              index={index}
              onNavigateToDetail={() => saveScrollPosition(shortlistKey)}
            />
          );

          if (index === 0) {
            return (
              <div key={candidate.id} ref={firstCardRef}>
                {card}
              </div>
            );
          }

          return <div key={candidate.id}>{card}</div>;
        })}
      </div>

      {!showAll && remaining.length > 0 && nextCandidate && (
        <div className="mt-5 space-y-3">
          <ShortlistCandidateCard candidate={nextCandidate} preview />

          <Button
            variant="secondary"
            size="lg"
            className="w-full"
            onClick={() => setShowAll(shortlistKey, true)}
          >
            View {remaining.length} more profiles
            <ChevronDown className="size-4" aria-hidden />
          </Button>
        </div>
      )}
    </motion.section>
  );
}
